// Breakdown — grouped per-bar / per-stream split of a figure.
//
// pending => renders "pending …" (never fake rows). Otherwise rows are capped
// at 8 by the engine and the "top N of M" truncation is surfaced honestly.
import React from "react";

import type { BreakdownResolution } from "./honestyEngine";
import type { BreakdownRow } from "../../../chatBot/models/machineEvidenceModels";
import { dt } from "../../../themes/redesignTokens";
import { EM_DASH, PLACEHOLDER_FG } from "./honestyTokens";
import { getUiLocale } from "../../../utils/relativeTime";

export interface BreakdownProps {
  resolution: BreakdownResolution | null;
}

const rowValue = (r: BreakdownRow): string => {
  if (r.value === null || r.value === undefined) return EM_DASH;
  const base = typeof r.value === "number" ? r.value.toLocaleString(getUiLocale()) : r.value;
  return r.unit ? `${base} ${r.unit}` : base;
};

export const Breakdown: React.FC<BreakdownProps> = ({ resolution }) => {
  if (!resolution) return null;

  if (resolution.pending) {
    return (
      <div className="msc-breakdown msc-breakdown-pending" style={{ color: PLACEHOLDER_FG, fontSize: 12 }}>
        {EM_DASH}{" "}
        <span style={{ fontStyle: "italic" }}>
          pending
          {resolution.pendingReason ? ` (${resolution.pendingReason})` : ""}
        </span>
      </div>
    );
  }

  return (
    <div className="msc-breakdown" style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {resolution.groupLabel ? (
        <span style={{ fontSize: 11, color: dt.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          by {resolution.groupLabel}
        </span>
      ) : null}
      {resolution.rows.map((r, i) => (
        <div
          key={`${r.label}-${i}`}
          className="msc-breakdown-row"
          style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12 }}
        >
          <span style={{ color: dt.textSecondary }}>{r.label}</span>
          <span style={{ fontFamily: dt.fontMono, color: dt.textPrimary }}>
            {rowValue(r)}
            {r.denominator ? (
              <span style={{ color: dt.textMuted }}>
                {" "}
                · {r.denominator.num.toLocaleString(getUiLocale())} / {r.denominator.den.toLocaleString(getUiLocale())}
              </span>
            ) : null}
          </span>
        </div>
      ))}
      {resolution.isTruncated ? (
        <span style={{ fontSize: 11, color: dt.textMuted, fontStyle: "italic" }}>
          top {resolution.shown} of {resolution.total}
        </span>
      ) : null}
    </div>
  );
};

export default Breakdown;
